/**
 * pinLogin.js — pure helpers for the student PIN login [PHASE-5 2026-10].
 *
 * Primary/middle pupils sign in with school code + student number + a 4-digit
 * PIN. An Arabic phone keyboard types Arabic-Indic digits (٠١٢٣…) or, on some
 * devices, Extended Arabic-Indic / Persian digits (۰۱۲۳…); the backend
 * accepts both too, but normalising here lets the form validate and lets the
 * remembered school code be stored in one spelling.
 */

import { safeStorage } from './safeStorage';

const ARABIC_INDIC_ZERO = 0x0660;
const PERSIAN_ZERO = 0x06f0;

/** Arabic-Indic and Persian digits → ASCII; every whitespace removed. */
export function toAsciiDigits(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/[٠-٩]/g, (ch) => String(ch.charCodeAt(0) - ARABIC_INDIC_ZERO))
    .replace(/[۰-۹]/g, (ch) => String(ch.charCodeAt(0) - PERSIAN_ZERO))
    .replace(/\s+/g, '');
}

export const PIN_LENGTH = 4;
const SCHOOL_CODE_RE = /^[0-9]{3,10}$/;
const STUDENT_NUMBER_RE = /^[0-9]{1,12}$/;
const PIN_RE = /^[0-9]{4}$/;

/** Keep only digits (after conversion), capped at `max` — for onChange. */
export function digitsOnly(value, max) {
  const digits = toAsciiDigits(value).replace(/[^0-9]/g, '');
  return typeof max === 'number' ? digits.slice(0, max) : digits;
}

/**
 * The request body for POST /auth/login/pin, or the i18n key of the first
 * problem. A missing PIN is an error: number-only login does not exist.
 */
export function buildPinLoginPayload({ schoolCode, studentNumber, pin }) {
  const school_number = toAsciiDigits(schoolCode);
  const student_number = toAsciiDigits(studentNumber);
  const pinDigits = toAsciiDigits(pin);
  if (!SCHOOL_CODE_RE.test(school_number)) return { error: 'pin_login_err_school_code' };
  if (!STUDENT_NUMBER_RE.test(student_number)) return { error: 'pin_login_err_student_number' };
  if (!PIN_RE.test(pinDigits)) return { error: 'pin_login_err_pin' };
  return { payload: { school_number, student_number, pin: pinDigits } };
}

/**
 * Turn a failed PIN login into { key, params } for t(). The server answers
 * 401 PIN_INVALID for every wrong combination (so it reveals nothing) and
 * 429 PIN_LOCKED with retry_after_minutes.
 */
export function pinLoginErrorKey(error) {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 429) {
    const minutes = Number(detail?.retry_after_minutes);
    if (Number.isFinite(minutes) && minutes > 0) {
      return { key: 'pin_login_locked', params: { minutes } };
    }
    return { key: 'pin_login_too_many', params: {} };
  }
  if (status === 401) return { key: 'pin_login_invalid', params: {} };
  if (status === 422) return { key: 'pin_login_err_pin', params: {} };
  if (status === 403 && detail && typeof detail === 'object' && detail.code) {
    return { key: null, params: {} }; // e.g. subscription lock: caller shows the server's message
  }
  return { key: 'error', params: {} };
}

// ── The school code is remembered per device ────────────────────────────
// Only the 3–10 digit school code, never the student number or the PIN.
// Storage can be blocked (private window, kiosk policy): every access is
// guarded and the form simply starts empty.
export const SCHOOL_CODE_STORAGE_KEY = 'edugate_pin_school_code';

export function loadRememberedSchoolCode(storage) {
  try {
    const s = storage || safeStorage('local');
    const v = s ? s.getItem(SCHOOL_CODE_STORAGE_KEY) : '';
    return v && SCHOOL_CODE_RE.test(v) ? v : '';
  } catch {
    return '';
  }
}

export function rememberSchoolCode(code, storage) {
  try {
    const s = storage || safeStorage('local');
    const v = toAsciiDigits(code);
    if (s && SCHOOL_CODE_RE.test(v)) s.setItem(SCHOOL_CODE_STORAGE_KEY, v);
  } catch {
    /* storage blocked — the code is simply typed again next time */
  }
}

export function forgetSchoolCode(storage) {
  try {
    const s = storage || safeStorage('local');
    if (s) s.removeItem(SCHOOL_CODE_STORAGE_KEY);
  } catch {
    /* storage blocked */
  }
}
